import { useMemo } from "react";
import { useDeviceInfo, DeviceCategory } from "./useDeviceInfo";

export interface ImageSizeConfig {
  phone: { width: number; height?: number; quality?: number };
  tablet: { width: number; height?: number; quality?: number };
  laptop: { width: number; height?: number; quality?: number };
  desktop: { width: number; height?: number; quality?: number };
  ultrawide: { width: number; height?: number; quality?: number };
}

const DEFAULT_IMAGE_SIZES: ImageSizeConfig = {
  phone: { width: 400, quality: 75 },
  tablet: { width: 600, quality: 80 },
  laptop: { width: 800, quality: 85 },
  desktop: { width: 1200, quality: 90 },
  ultrawide: { width: 1600, quality: 95 },
};

interface UseResponsiveImageOptions {
  /** Custom size configuration */
  sizes?: Partial<ImageSizeConfig>;
  /** Whether to consider pixel ratio for retina displays */
  useRetina?: boolean;
}

interface ResponsiveImageResult {
  /** Optimal width for current device */
  width: number;
  /** Optimal height (if specified) */
  height?: number;
  /** Optimal quality (0-100) */
  quality: number;
  /** Device category */
  deviceCategory: DeviceCategory;
  /** Whether device is retina */
  isRetina: boolean;
  /** Suggested srcset widths for responsive images */
  srcSetWidths: number[];
  /** Get optimized URL for image services that support URL params */
  getOptimizedUrl: (baseUrl: string) => string;
}

/**
 * Hook to get optimal image dimensions based on device category
 */
export function useResponsiveImage(
  options: UseResponsiveImageOptions = {}
): ResponsiveImageResult {
  const { sizes = {}, useRetina = true } = options;
  const { deviceCategory, isRetina, pixelRatio } = useDeviceInfo();

  return useMemo(() => {
    const mergedSizes = { ...DEFAULT_IMAGE_SIZES, ...sizes };
    const config = mergedSizes[deviceCategory];
    
    // Adjust for retina displays
    const retinaMultiplier = useRetina && isRetina ? Math.min(pixelRatio, 2) : 1;
    const width = Math.round(config.width * retinaMultiplier);
    const height = config.height ? Math.round(config.height * retinaMultiplier) : undefined;
    const quality = config.quality || 85;

    // Generate srcset widths for responsive images
    const srcSetWidths = [
      mergedSizes.phone.width,
      mergedSizes.tablet.width,
      mergedSizes.laptop.width,
      mergedSizes.desktop.width,
      mergedSizes.ultrawide.width,
    ];

    // Helper to generate optimized URL for common image CDNs
    const getOptimizedUrl = (baseUrl: string): string => {
      // If it's a Supabase storage URL, add transformation params
      if (baseUrl.includes('supabase.co/storage')) {
        const separator = baseUrl.includes('?') ? '&' : '?';
        return `${baseUrl}${separator}width=${width}${height ? `&height=${height}` : ''}&quality=${quality}`;
      }
      
      // If it's an Unsplash URL
      if (baseUrl.includes('unsplash.com')) {
        const separator = baseUrl.includes('?') ? '&' : '?';
        return `${baseUrl}${separator}w=${width}${height ? `&h=${height}` : ''}&q=${quality}&auto=format`;
      }

      // If it's a Cloudinary URL
      if (baseUrl.includes('cloudinary.com')) {
        // Insert transformation before /upload/
        return baseUrl.replace(
          '/upload/',
          `/upload/w_${width}${height ? `,h_${height}` : ''},q_${quality},f_auto/`
        );
      }

      // Return original URL for other sources
      return baseUrl;
    };

    return {
      width,
      height,
      quality,
      deviceCategory,
      isRetina,
      srcSetWidths,
      getOptimizedUrl,
    };
  }, [deviceCategory, isRetina, pixelRatio, sizes, useRetina]);
}

/**
 * Get image loading priority based on device
 */
export function useImageLoadingStrategy() {
  const { deviceCategory, isTouchDevice } = useDeviceInfo();

  return useMemo(() => {
    // On phones and touch devices, be more aggressive with lazy loading
    const shouldLazyLoad = deviceCategory === "phone" || deviceCategory === "tablet";
    
    // Higher quality images on larger screens
    const preferHighQuality = deviceCategory === "desktop" || deviceCategory === "ultrawide";

    // Number of images to eagerly load
    const eagerLoadCount = deviceCategory === "phone" ? 2 : deviceCategory === "tablet" ? 4 : 6;

    return {
      shouldLazyLoad,
      preferHighQuality,
      eagerLoadCount,
      loading: shouldLazyLoad ? "lazy" as const : "eager" as const,
      decoding: "async" as const,
    };
  }, [deviceCategory, isTouchDevice]);
}

/**
 * Generate sizes attribute for responsive images
 */
export function useImageSizes(customSizes?: string): string {
  const { deviceCategory } = useDeviceInfo();

  return useMemo(() => {
    if (customSizes) return customSizes;

    // Default responsive sizes based on common layouts
    switch (deviceCategory) {
      case "phone":
        return "100vw";
      case "tablet":
        return "50vw";
      case "laptop":
        return "33vw";
      case "desktop":
      case "ultrawide":
        return "25vw";
      default:
        return "(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw";
    }
  }, [deviceCategory, customSizes]);
}
